// Pitch 8, "shareable fights": a run dies with the tab, so this module turns one into a URL. Spec §9
// names the replay contract this leans on - `{ao, scenario, seed, dials, orderLog}` fully determines
// every tick (`Sim.hash()` proves it, `Sim.replay` reproduces it) - and adds only what the debrief and
// the planning stage need to reconstruct the surrounding session: the drawn control graphics, the
// player's opening plan, where the run actually ended, the ruleset it played under, and (optionally) the
// model answers it narrated with.
//
// Encoding is JSON -> a compact array-based wire shape (so deflate has less to do) -> `deflate-raw`
// (the browser's `CompressionStream`/`DecompressionStream`, no polyfill) -> base64url, which is what
// lands in the `?fight=` query string. `encodeFight`/`decodeFight` are the only two functions that know
// about the wire shape; every other caller reads and writes the friendly `FightRecord`.

import type { CardId, Dials, Graphic, GraphicKind, Scenario, Target, TargetKind } from '../sim/types';
import { Grid } from '../sim/grid';
import { Sim } from '../sim/sim';
import type { OrderLogEntry } from '../sim/sim';
import type { RulesetInfo } from '../sim/worker';
import type { EndedPayload } from '../state/debrief';
import type { PlanOrder } from '../state/store';
import { cacheSnapshot, type CacheEntry } from '../ai/cache';

/** The current record version. Bumped only if the wire shape ever has to change incompatibly. */
export const FIGHT_RECORD_VERSION = 1;

/** Soft budget for the `llm` map (item 4): a shared fight is meant to be a URL, not a database dump. */
export const LLM_CACHE_MAX_BYTES = 32 * 1024;

/**
 * Everything a browser with no memory of this run needs to watch it happen again or pick up the plan.
 * `hash` is `Sim.hash()` at `endedTick`, computed by replaying `orderLog` on the fitted scenario at
 * share time (see `buildFightRecord`) - the same computation `Sim.replay` used to reproduce it will
 * repeat after the round trip through the URL, so the two can be compared ("verified identical").
 */
export interface FightRecord {
  v: 1;
  ao: string;
  seed: number;
  dials: Dials;
  /** Control graphics the player drew during planning - only ids starting `drawn-` (see
   * `src/map/draw.ts`); the fitted scenario's own graphics (phase lines, NAIs, the objective) are
   * reproduced for free by re-fitting the same seed onto the same AO. */
  drawnGraphics: Graphic[];
  plan: PlanOrder[];
  orderLog: OrderLogEntry[];
  /** The tick the run ended on (`Snapshot.tick` at end) - `Sim.replay`'s `untilTick`. */
  endedTick: number;
  ruleset: RulesetInfo;
  hash: string;
  /** Recorded model answers, keyed by `src/ai/cache.ts`'s `cacheKey` - seeded into the viewer's cache
   * before anything narrates, so a shared fight reads the sharer's own words instead of asking the
   * model (and demos correctly with no network at all). Capped at `LLM_CACHE_MAX_BYTES`; the most
   * recent recordings are kept first. */
  llm?: Record<string, CacheEntry>;
}

// ---- the wire shape ---------------------------------------------------------------------------------
// Arrays and small integer indices instead of repeated object keys and enum strings: on a synthetic
// 300-order log this alone takes the deflated, base64url-encoded record from ~4.6 KB to ~3.4 KB before
// the rest of the record is even added (measured in tests/share/fight.test.ts). No lossy step - every
// order log entry and every field round-trips exactly, because dropping anything (an idea floated for
// this budget: drop rejected orders) would shift every later order's id in `OrderBook` and change the
// very hash this feature exists to reproduce (see `src/sim/orders.ts` `issue`). A record over budget is
// a real fight with a lot of orders in it, not a bug to quantize away.

const CARD_IDS: CardId[] = [
  'move', 'attack', 'defend', 'screen', 'withdraw',
  'fireMission', 'smoke', 'suppress',
  'launchUas', 'recon', 'observe',
  'resupply', 'casevac',
  'cancel', 'priorityFires', 'mainEffort', 'frago',
];
const TARGET_KINDS: TargetKind[] = ['point', 'graphic', 'unit', 'contact', 'none', 'text'];
const GRAPHIC_KINDS: GraphicKind[] = ['axis', 'obj', 'pl', 'nai', 'aa'];

type WireTarget = [number, number | string | null];

function encTarget(target: Target): WireTarget {
  const k = TARGET_KINDS.indexOf(target.kind);
  switch (target.kind) {
    case 'point': return [k, target.cell ?? null];
    case 'graphic': return [k, target.graphicId ?? null];
    case 'unit': case 'contact': return [k, target.unitId ?? null];
    case 'text': return [k, target.text ?? null];
    default: return [k, null];
  }
}

function decTarget([k, v]: WireTarget): Target {
  const kind = TARGET_KINDS[k];
  switch (kind) {
    case 'point': return { kind, cell: v as number };
    case 'graphic': return { kind, graphicId: v as string };
    case 'unit': case 'contact': return { kind, unitId: v as number };
    case 'text': return { kind, text: v as string };
    default: return { kind };
  }
}

/** `[t, side, unitId, cardIdx, targetKindIdx, targetVal, playIdIdx]`; `playIdIdx` is `-1` for "none". */
type WireOrderLogEntry = [number, number, number, number, number, number | string | null, number];

/** `[unitId, cardIdx, targetKindIdx, targetVal]`. */
type WirePlanOrder = [number, number, number, number | string | null];

/** `[id, kindIdx, name, flatPts, radiusM | null]`; `flatPts` is `[x0, y0, x1, y1, ...]`. */
type WireGraphic = [string, number, string, number[], number | null];

interface WireRecord {
  v: 1;
  ao: string;
  seed: number;
  dials: Dials;
  dg: WireGraphic[];
  plan: WirePlanOrder[];
  ol: WireOrderLogEntry[];
  pid: string[];
  endedTick: number;
  ruleset: RulesetInfo;
  hash: string;
  llm?: Record<string, CacheEntry>;
}

function encGraphic(g: Graphic): WireGraphic {
  const pts: number[] = [];
  for (const p of g.pts) { pts.push(p.x, p.y); }
  return [g.id, GRAPHIC_KINDS.indexOf(g.kind), g.name, pts, g.radiusM ?? null];
}

function decGraphic([id, kindIdx, name, pts, radiusM]: WireGraphic): Graphic {
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) points.push({ x: pts[i], y: pts[i + 1] });
  return { id, kind: GRAPHIC_KINDS[kindIdx], name, pts: points, radiusM: radiusM ?? undefined };
}

function toWire(rec: FightRecord): WireRecord {
  const playIds: string[] = [];
  const playIdIdx = (p: string | undefined): number => {
    if (p === undefined) return -1;
    const i = playIds.indexOf(p);
    if (i >= 0) return i;
    playIds.push(p);
    return playIds.length - 1;
  };
  const ol: WireOrderLogEntry[] = rec.orderLog.map((e) => {
    const [tk, tv] = encTarget(e.target);
    const cardIdx = CARD_IDS.indexOf(e.card);
    if (cardIdx < 0) throw new Error(`encodeFight: unknown card id "${e.card}"`);
    return [e.t, e.side, e.unitId, cardIdx, tk, tv, playIdIdx(e.playId)];
  });
  const plan: WirePlanOrder[] = rec.plan.map((o) => {
    const [tk, tv] = encTarget(o.target);
    const cardIdx = CARD_IDS.indexOf(o.card);
    if (cardIdx < 0) throw new Error(`encodeFight: unknown card id "${o.card}"`);
    return [o.unitId, cardIdx, tk, tv];
  });
  return {
    v: 1,
    ao: rec.ao,
    seed: rec.seed,
    dials: rec.dials,
    dg: rec.drawnGraphics.map(encGraphic),
    plan,
    ol,
    pid: playIds,
    endedTick: rec.endedTick,
    ruleset: rec.ruleset,
    hash: rec.hash,
    llm: rec.llm,
  };
}

function fromWire(w: WireRecord): FightRecord {
  const orderLog: OrderLogEntry[] = w.ol.map(([t, side, unitId, cardIdx, tk, tv, pidx]) => ({
    t, side: side as 0 | 1, unitId, card: CARD_IDS[cardIdx], target: decTarget([tk, tv]),
    playId: pidx >= 0 ? w.pid[pidx] : undefined,
  }));
  const plan: PlanOrder[] = w.plan.map(([unitId, cardIdx, tk, tv]) => ({
    unitId, card: CARD_IDS[cardIdx], target: decTarget([tk, tv]),
  }));
  return {
    v: 1,
    ao: w.ao,
    seed: w.seed,
    dials: w.dials,
    drawnGraphics: w.dg.map(decGraphic),
    plan,
    orderLog,
    endedTick: w.endedTick,
    ruleset: w.ruleset,
    hash: w.hash,
    llm: w.llm,
  };
}

// ---- compression + base64url --------------------------------------------------------------------------

/** A malformed `inflate` input rejects both the write and (separately) the readable side - the readable
 * side's rejection is what the caller awaits below and turns into "not a valid fight"; the write side's
 * own rejection is caught here purely so Node/the browser never reports it as unhandled. */
function writeAndClose(writer: WritableStreamDefaultWriter<BufferSource>, bytes: Uint8Array): void {
  writer.write(bytes as BufferSource).catch(() => {});
  writer.close().catch(() => {});
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate-raw');
  writeAndClose(cs.writable.getWriter(), bytes);
  return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  writeAndClose(ds.writable.getWriter(), bytes);
  return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

/** Browser and Node both have `btoa`/`atob` globally (Node since v16, as a `Buffer`-backed shim); no
 * dependency either way. */
function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** JSON -> compact wire shape -> `deflate-raw` -> base64url. */
export async function encodeFight(rec: FightRecord): Promise<string> {
  const json = JSON.stringify(toWire(rec));
  const compressed = await deflate(new TextEncoder().encode(json));
  return toBase64Url(compressed);
}

/** The reverse of `encodeFight`. Throws on malformed input - callers at the edge (`fightFromUrl`, the
 * landing card) turn that into "this link is not a valid fight" rather than a crash. */
export async function decodeFight(s: string): Promise<FightRecord> {
  const compressed = fromBase64Url(s);
  const json = new TextDecoder().decode(await inflate(compressed));
  const wire = JSON.parse(json) as WireRecord;
  if (wire.v !== 1) throw new Error(`decodeFight: unsupported record version ${String(wire.v)}`);
  return fromWire(wire);
}

/** The link the SHARE chip copies: this tab's origin and path, with the record as `?fight=`. */
export async function fightToUrl(rec: FightRecord, location: { origin: string; pathname: string } = window.location): Promise<string> {
  const b64 = await encodeFight(rec);
  return `${location.origin}${location.pathname}?fight=${b64}`;
}

/** Reads and decodes `?fight=` from the current page, or `null` if there is none or it does not parse. */
export async function fightFromUrl(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): Promise<FightRecord | null> {
  const raw = new URLSearchParams(search).get('fight');
  if (!raw) return null;
  try {
    return await decodeFight(raw);
  } catch {
    return null;
  }
}

// ---- building a record from a finished run -------------------------------------------------------

/**
 * Assembles a `FightRecord` from the debrief payload plus the store state the player planned and fought
 * with. `hash` is computed here by replaying `payload.orderLog` on the same grid and scenario (the exact
 * mechanism `Sim.replay` uses) rather than trusting a value carried out of the worker - determinism is
 * the contract (spec §9), so this is the same number the live run ended on, and it is also exactly what
 * a viewer's "WATCH THE REPLAY" will recompute after the round trip through the URL.
 */
export function buildFightRecord(args: {
  grid: Grid;
  scenario: Scenario;
  dials: Dials;
  plan: PlanOrder[];
  payload: EndedPayload;
  includeLlm?: boolean;
}): FightRecord {
  const { grid, scenario, dials, plan, payload } = args;
  const endedTick = payload.snap.tick;
  const replayed = Sim.replay(
    grid, scenario, dials, scenario.seed, payload.orderLog, endedTick,
    dials.rules.preset, dials.rules.overrides,
  );
  return {
    v: 1,
    ao: scenario.ao,
    seed: scenario.seed,
    dials,
    drawnGraphics: scenario.graphics.filter((g) => g.id.startsWith('drawn-')),
    plan,
    orderLog: payload.orderLog,
    endedTick,
    ruleset: payload.ruleset,
    hash: replayed.hash(),
    llm: args.includeLlm === false ? undefined : buildLlmCache(),
  };
}

/**
 * This browser's most recently recorded model answers, most recent first, kept until
 * `LLM_CACHE_MAX_BYTES` of JSON is spent. `src/ai/audit.ts`'s ring buffer records what a call was *for*
 * but not the request or the answer itself (it exists to show the model's discipline, not to replay it),
 * so there is no way to filter this precisely to "the debrief narrative and the S-2 assessments" without
 * reaching into `src/ai/client.ts` or the debrief/advisor call sites - both out of scope for this task.
 * The approximation (this session's own cache, newest entries first, capped) is what actually ships: it
 * still seeds the viewer's cache with whatever this run narrated with, debrief and S-2 included, plus
 * whatever else this tab cached alongside them.
 */
export function buildLlmCache(maxBytes: number = LLM_CACHE_MAX_BYTES): Record<string, CacheEntry> | undefined {
  const all = cacheSnapshot();
  const keys = Object.keys(all);
  if (keys.length === 0) return undefined;
  const out: Record<string, CacheEntry> = {};
  let size = 2; // "{}"
  for (let i = keys.length - 1; i >= 0; i--) {
    const k = keys[i];
    const piece = JSON.stringify(k) + ':' + JSON.stringify(all[k]) + ',';
    if (size + piece.length > maxBytes) continue; // a smaller, older entry might still fit
    out[k] = all[k];
    size += piece.length;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
